import { Badge, Box, Text, WhiteCard } from '@elkaso-app/web-design';
import { IInvoiceScanItemData } from 'apis/invoice-scanning';
import { getMatchStatusStyle, matchStatusLabels, MatchStatus } from '../utils/invoice-scan-status';

interface ItemsTableProps {
  items: IInvoiceScanItemData[];
}

const HeaderCell = ({ children }: { children: React.ReactNode }) => (
  <Box
    as='th'
    textAlign='left'
    px='md'
    py='sm'
    borderBottom='1px'
    borderColor='gray.200'
    color='gray.500'
    fontSize='xs'
    fontWeight='semibold'
    textTransform='uppercase'>
    {children}
  </Box>
);

const TableCell = ({ children }: { children: React.ReactNode }) => (
  <Box as='td' px='md' py='sm' borderBottom='1px' borderColor='gray.100' fontSize='sm'>
    {children}
  </Box>
);

export const ItemsTable = ({ items }: ItemsTableProps) => {
  if (!items || items.length === 0) {
    return (
      <WhiteCard p='lg'>
        <Text fontSize='lg' fontWeight='semibold' mb='md'>
          Extracted Items
        </Text>
        <Text fontSize='sm' color='gray.400'>
          No items extracted from this invoice.
        </Text>
      </WhiteCard>
    );
  }

  return (
    <WhiteCard p='lg'>
      <Text fontSize='lg' fontWeight='semibold' mb='md'>
        Extracted Items ({items.length})
      </Text>
      <Box overflowX='auto'>
        <Box as='table' w='100%' fontSize='sm'>
          <Box as='thead'>
            <Box as='tr'>
              <HeaderCell>Description</HeaderCell>
              <HeaderCell>Qty</HeaderCell>
              <HeaderCell>Unit</HeaderCell>
              <HeaderCell>Unit Price</HeaderCell>
              <HeaderCell>Total Price</HeaderCell>
              <HeaderCell>Match Status</HeaderCell>
              <HeaderCell>Matched Item</HeaderCell>
              <HeaderCell>Confidence</HeaderCell>
              <HeaderCell>Discrepancy</HeaderCell>
            </Box>
          </Box>
          <Box as='tbody'>
            {items.map((item) => {
              const matchStyle = getMatchStatusStyle(item.matchStatus);
              return (
                <Box as='tr' key={item.id} _hover={{ bg: 'gray.50' }}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.quantity ?? '---'}</TableCell>
                  <TableCell>{item.unit || '---'}</TableCell>
                  <TableCell>{item.unitPrice?.toFixed(2) ?? '---'}</TableCell>
                  <TableCell>{item.totalPrice?.toFixed(2) ?? '---'}</TableCell>
                  <TableCell>
                    <Badge
                      px='sm'
                      py='xs'
                      borderRadius='md'
                      fontSize='xs'
                      color={matchStyle.color}
                      bg={matchStyle.bgColor}>
                      {matchStatusLabels[item.matchStatus as MatchStatus] || item.matchStatus}
                    </Badge>
                  </TableCell>
                  <TableCell>{item.matchedOrderItemId ?? '---'}</TableCell>
                  <TableCell>{item.matchConfidence !== null ? `${item.matchConfidence}%` : '---'}</TableCell>
                  <TableCell>
                    {item.discrepancyType ? (
                      <Text fontSize='xs' color='orange.600'>
                        {item.discrepancyType.replace(/_/g, ' ')}
                      </Text>
                    ) : (
                      '---'
                    )}
                  </TableCell>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </WhiteCard>
  );
};
